import { createUseCRUDService, ICRUDService } from '../../../reusableFeatures';
import { backendServices } from '../../../services';
import { PortalType, RecursivePartial } from '../../../types';
import { validateData } from '../../../ui';
import { RoleTypeFormData } from './RoletypeFormData';

export class RoleTypeCRUDService implements ICRUDService<number, RoleTypeFormData> {
    private api = new backendServices.Apis.RoleTypeApi();

    constructor(private companyId: number | null, private viewingFrom: PortalType) {}

    get defaultState(): RoleTypeFormData {
        let defaultPortalType = PortalType.TransceptaManager;

        if (this.viewingFrom === PortalType.SupplierPortal) {
            defaultPortalType = PortalType.SupplierPortal;
        }

        if (this.viewingFrom === PortalType.TransceptaManager || this.viewingFrom === PortalType.CSP) {
            defaultPortalType = PortalType.TransceptaManager;
        }

        return {
            id: null,
            buyerCompanyId: this.companyId,
            description: '',
            roleName: '',
            permissions: [],
            portalType: defaultPortalType,
        };
    }

    get key() {
        return 'RoleType';
    }

    get invalidateKeys(): string[] {
        // For the search page and the list of roles in UserCRUD
        return ['RoleTypeSearch', 'RoleTypes'];
    }

    async fetchEntity(id: number) {
        const res = await this.api.getRoleType(id, {});

        if (!res.data) {
            throw new Error('Cannot find RoleType');
        }

        return this.mapFromViewModel(res.data);
    }

    async createEntity(state: RoleTypeFormData): Promise<number> {
        validateData((errors) => {
            if (!state.roleName) {
                errors.push('Role Name is required to create a Role Type');
            }

            if (!state.description) {
                errors.push('Description is required to create a Role Type');
            }

            if (!state.portalType) {
                errors.push('Portal is required to create a Role Type');
            }
        });

        const res = await this.api.postRoleType(this.mapToViewModel(state));

        return res.data.ID;
    }

    async updateEntity(ruleId: number, state: RoleTypeFormData) {
        validateData((errors) => {
            if (!state.roleName) {
                errors.push('Role Name is required to update a Role Type');
            }

            if (!state.description) {
                errors.push('Description is required to update a Role Type');
            }

            if (!state.portalType) {
                errors.push('Portal is required to update a Role Type');
            }
        });

        await this.api.putRoleType(ruleId, this.mapToViewModel(state));
    }

    async deleteEntity(ruleId: number): Promise<void> {
        await this.api.deleteRoleType(ruleId);
    }

    private mapToViewModel(state: RoleTypeFormData): RecursivePartial<backendServices.ViewModels.RoleTypeViewModel> {
        return {
            ID: state.id ?? undefined,
            Name: state.roleName,
            CompanyId: state.buyerCompanyId,
            Description: state.description,
            RoleType: 0,
            RoleTypePermissions: state.permissions.map((p) => ({ PermissionId: p, RoleTypeId: state.id ?? undefined })),
            PortalType: state.portalType,
        };
    }

    private mapFromViewModel(vm: backendServices.ViewModels.RoleTypeViewModel): RoleTypeFormData {
        return {
            roleName: vm.Name,
            id: vm.ID,
            buyerCompanyId: vm.CompanyId,
            description: vm.Description,
            permissions: vm.RoleTypePermissions.map((p) => p.PermissionId),
            portalType: vm.PortalType,
        };
    }
}

export const useRoleTypeCRUDService = createUseCRUDService(RoleTypeCRUDService);
